"use client";

import { NodeIconTile } from "@/components/ui/node-icon-tile";
import { CardIconTile } from "@/components/workflow/CardIconTile";
import {
  CARD_TILE_ICON_CLASS,
  CARD_TILE_INNER_CLASS,
  CARD_TILE_WRAP_CLASS,
  cardIconKey,
} from "@/components/workflow/CardIconStrip";
import { useBrandCardIcons } from "@/hooks/useWorkflowCardIcons";
import { cn } from "@/lib/utils";
import { getTemplateUrl } from "@/lib/templates/templateUrl";
import type { Template } from "@/stores/templatesStore";
import Link from "next/link";
import { useMemo } from "react";

interface FeaturedTemplateCardProps {
  template: Template;
  className?: string;
}

const MAX_APPS = 3;

/**
 * Dashboard "Featured on B3OS" card — same icon-derivation as WorkflowCard
 * (ordered BFS across definition.nodes, deduped by visual key). Node icons
 * render via NodeIcon (b3os-app-flat) so nodes without a brand logo get the
 * AI-generated icon instead of a fabricated `/logo/namespace` URL.
 */
export function FeaturedTemplateCard({ template, className }: FeaturedTemplateCardProps) {
  const { brandIcons, triggers, actions, connectorTypes, chainInfoMap } = useBrandCardIcons(template);
  const visible = useMemo(() => brandIcons.slice(0, MAX_APPS), [brandIcons]);

  const description = template.description?.split(/[.!?]/)[0]?.trim() ?? "";
  const short = description ? (description.endsWith(".") ? description : `${description}.`) : "";

  // If the card would otherwise render a single tile and that tile is not the
  // template's own AI-generated icon (it never is today — cardIcons are always
  // node/connector/chain), prepend the template icon so the strip reads as
  // "this template → that brand" instead of one lonely logo. Keeps the card
  // identifiable even when the workflow only surfaces one recognizable brand.
  const showTemplateLead = visible.length === 1;
  const totalTiles = visible.length + (showTemplateLead ? 1 : 0);

  return (
    <Link
      href={getTemplateUrl(template)}
      className={cn(
        "border-ds-border-default bg-ds-surface-card rounded-ds-md shadow-ds-soft ease-out-quart group block w-[300px] shrink-0 border transition-[border-color,box-shadow,transform] duration-150",
        "hover:border-ds-neutral-300 dark:hover:border-ds-neutral-600 hover:shadow-ds-elev-2 hover:-translate-y-px motion-reduce:transform-none",
        className,
      )}
    >
      <div className="flex flex-col gap-5 px-2 pb-5 pt-2">
        <div className="bg-ds-surface-sunken border-ds-border rounded-ds-xs relative flex h-[105px] items-center justify-center overflow-hidden border">
          {visible.length > 0 ? (
            <div className="relative flex items-center gap-4">
              <DashedConnector count={totalTiles} />
              {showTemplateLead && (
                <div style={{ zIndex: totalTiles }} className="relative">
                  <TemplateIconTile
                    template={template}
                    wrapClassName={CARD_TILE_WRAP_CLASS}
                    innerClassName={CARD_TILE_INNER_CLASS}
                  />
                </div>
              )}
              {visible.map((icon, i) => (
                <div key={cardIconKey(icon)} style={{ zIndex: visible.length - i }} className="relative">
                  <CardIconTile
                    icon={icon}
                    triggers={triggers}
                    actions={actions}
                    connectorTypes={connectorTypes}
                    chainInfoMap={chainInfoMap}
                    wrapClassName={CARD_TILE_WRAP_CLASS}
                    innerClassName={CARD_TILE_INNER_CLASS}
                    iconClassName={CARD_TILE_ICON_CLASS}
                  />
                </div>
              ))}
            </div>
          ) : (
            <TemplateIconFallback template={template} />
          )}
        </div>
        <div className="flex flex-col items-start gap-1 px-3.5">
          <p className="text-ds-text-primary line-clamp-1 w-full text-lg font-medium leading-tight">{template.name}</p>
          <p className="text-ds-text-muted line-clamp-2 min-h-[2lh] w-full text-sm leading-tight">{short}</p>
        </div>
      </div>
    </Link>
  );
}

function TemplateIconFallback({ template }: { template: Template }) {
  // Matches the multi-tile treatment so single-icon templates look consistent.
  return (
    <NodeIconTile
      size="md"
      id={template.id}
      subject={template.name}
      context={template.description || ""}
      template="b3os-app-flat"
      alt={template.name}
    />
  );
}

/** Same AI-generated template tile as the empty-state fallback, but with
 * explicit wrap/inner classes so it sits flush next to the other CardIconTile
 * tiles in the strip. */
function TemplateIconTile({
  template,
  wrapClassName,
  innerClassName,
}: {
  template: Template;
  wrapClassName: string;
  innerClassName: string;
}) {
  return (
    <NodeIconTile
      size="md"
      id={template.id}
      subject={template.name}
      context={template.description || ""}
      template="b3os-app-flat"
      alt={template.name}
      wrapClassName={wrapClassName}
      innerClassName={innerClassName}
      iconClassName={CARD_TILE_ICON_CLASS}
    />
  );
}

function DashedConnector({ count }: { count: number }) {
  if (count < 2) return null;
  return (
    <span
      aria-hidden
      className="border-ds-neutral-400 absolute left-[15px] right-[15px] top-1/2 -translate-y-1/2 border-t border-dashed"
      style={{ zIndex: 0 }}
    />
  );
}
